// CSRF token management for SPA requests to Rails.
//
// Rails resets the session (and CSRF token) on sign in/out to prevent
// session fixation. The token in the <meta> tag becomes stale, so we:
// 1. Update it from JSON responses that include a fresh token
// 2. Auto-retry on 422 by fetching a fresh token from the server

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute('content') || ''
}

export function updateCSRFToken(token: string): void {
  const meta = document.querySelector('meta[name="csrf-token"]')
  if (meta) {
    meta.setAttribute('content', token)
  }
}

async function refreshCSRFToken(): Promise<void> {
  const response = await fetch('/', {
    credentials: 'same-origin',
    headers: { 'Accept': 'text/html' },
  })
  const html = await response.text()
  const match = html.match(/<meta name="csrf-token" content="([^"]+)"/)
  if (match?.[1]) {
    updateCSRFToken(match[1])
  }
}

export function fetchOptions(method: string = 'GET'): RequestInit {
  return {
    method,
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-CSRF-Token': getCSRFToken(),
    },
  }
}

// Fetch with automatic CSRF retry: if a 422 is returned (stale token),
// refresh the CSRF token and retry the request once.
export async function fetchWithCSRFRetry(url: string, options: RequestInit): Promise<Response> {
  const response = await fetch(url, options)
  if (response.status === 422) {
    await refreshCSRFToken()
    const retryOptions = {
      ...options,
      headers: {
        ...options.headers,
        'X-CSRF-Token': getCSRFToken(),
      },
    }
    return fetch(url, retryOptions)
  }
  return response
}
